import { CANONICAL_ORIGIN } from '@/config'

interface SeoHeadProps {
  /** Page title — browser tab + SERP title. */
  title: string
  /** Per-route meta description. */
  description?: string
  /** Path used to build the canonical URL, e.g. "/codex" → https://corsalia.net/codex. */
  canonicalPath?: string
  /**
   * Set on vitrine pages that must not be indexed (e.g. /legal). The whole game
   * host (play.corsalia.net) is noindexed at the HTTP layer (X-Robots-Tag in
   * Caddy), so this prop is only for apex pages kept out of the index.
   */
  noindex?: boolean
}

/**
 * Per-route <head> metadata via React 19 native metadata hoisting (no
 * react-helmet): the <title>/<meta>/<link> rendered here are hoisted into
 * <head>. The build-time prerender (lot 23b) snapshots these into static HTML
 * so non-JS crawlers (AI bots, social scrapers) see them too.
 */
export function SeoHead({ title, description, canonicalPath, noindex }: SeoHeadProps) {
  // During build-time prerender (no DOM) the per-route <head> is templated by
  // scripts/prerender.mjs from the same ROUTE_SEO config, so render nothing here
  // to avoid duplicating the tags inside the static <body>. On the client, React
  // 19 hoists the tags below into <head> as usual.
  if (typeof window === 'undefined') return null

  return (
    <>
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      {canonicalPath ? <link rel="canonical" href={`${CANONICAL_ORIGIN}${canonicalPath}`} /> : null}
      {noindex ? <meta name="robots" content="noindex, follow" /> : null}
    </>
  )
}
